import React, { useState, useEffect, useRef } from 'react';
import { getChatHistoryApi } from '../../services/supportManagement';
import { dateFormatWithTime } from '../../utils/dateFormat';
import AudioPlayer from './AudioPlayer';

const ChatBox = ({ threadId, userName, userId, onClose, isConsultant = false, recordingUrl = "", orderId = "", consultationMode = "" }) => {
  const recordingOnly = Boolean(recordingUrl) && String(consultationMode || "").toLowerCase() === "audio";
  const [allMessages, setAllMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [shouldMaintainScroll, setShouldMaintainScroll] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  const chatContainerRef = useRef(null);

  // Fetch chat history
  const fetchChatHistory = async (isLoadMore = false) => {
    if (recordingOnly) return;
    if (!threadId) return;
    
    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const response = await getChatHistoryApi({ 
        threadId, 
        page: currentPage, 
        limit: 10 
      });
      
      const data = response?.data?.result?.messages || [];
      
      if (isLoadMore) {
        // Load more: prepend previous messages to existing ones
        setAllMessages(prevMessages => {
          // Remove duplicates based on message ID
          const existingIds = new Set(prevMessages.map(msg => msg._id));
          const newMessages = data.filter(msg => !existingIds.has(msg._id));
          
          const combinedMessages = [...newMessages, ...prevMessages];
          return combinedMessages;
        });
      } else {
        // Initial load: set messages directly
        setAllMessages(data);
        // Scroll to bottom after setting messages
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 100);
      }
      
      setHasMoreMessages(response?.data?.result?.totalPages > currentPage);
    } catch (error) {
      console.error("Error fetching chat history:", error);
    } finally {
      setIsLoadingMore(false);
      setIsLoading(false);
    }
  };


  // Scroll to bottom
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      setShouldMaintainScroll(true);
      setScrollOffset(0);
    }
  };

  // Handle scroll position when messages are loaded
  // useEffect(() => {
  //   if (allMessages.length > 0 && chatContainerRef.current) {
  //     const container = chatContainerRef.current;
      
  //     if (shouldMaintainScroll) {
  //       // Maintain scroll position by keeping the same distance from bottom
  //       requestAnimationFrame(() => {
  //         setTimeout(() => {
  //           try {
  //             const newScrollHeight = container.scrollHeight;
  //             const newScrollTop = newScrollHeight - container.clientHeight - scrollOffset;
              
  //             // Ensure we don't scroll beyond the content
  //             if (newScrollTop >= 0 && newScrollTop <= newScrollHeight) {
  //               container.scrollTop = newScrollTop;
  //               console.log('Maintaining scroll position - new scroll top:', newScrollTop);
  //             } else {
  //               // Fallback: scroll to a reasonable position
  //               container.scrollTop = Math.max(0, newScrollHeight - container.clientHeight - 100);
  //               console.log('Using fallback scroll position');
  //             }
  //           } catch (error) {
  //             console.error('Error maintaining scroll position:', error);
  //             // Fallback: scroll to bottom
  //             container.scrollTop = container.scrollHeight;
  //           }
  //           setShouldMaintainScroll(false);
  //         }, 200);
  //       });
  //     } else {
  //       // Scroll to bottom for initial load or new messages
  //       container.scrollTop = container.scrollHeight;
  //     }
  //   }
  // }, [allMessages, shouldMaintainScroll, scrollOffset]);

  // Handle scroll events to show/hide scroll to bottom button and auto-load more messages
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollToBottom(!isNearBottom);
      
      // Auto-load more messages when scrolling near the top (trigger earlier)
      if (scrollTop < 200 && hasMoreMessages && !isLoadingMore && !isLoading) {
        setCurrentPage(prev => prev + 1);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, isLoadingMore, isLoading]);

  // Load more messages when page changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchChatHistory(true);
    }
  }, [currentPage]);

  // Initial load
  useEffect(() => {
    if (threadId && !recordingOnly) {
      setCurrentPage(1);
      setHasMoreMessages(true);
      setAllMessages([]);
      setShouldMaintainScroll(false);
      setScrollOffset(0);
      fetchChatHistory();
    } else {
      setCurrentPage(1);
      setHasMoreMessages(false);
      setAllMessages([]);
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [threadId, recordingOnly]);



  // Audio Player Component
  

  return (
    <div className="chat-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="chat-modal" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>{userName}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Chat Container */}
        <div 
          ref={chatContainerRef}
          style={{ 
            flex: 1,
            maxHeight: '500px', 
            overflowY: 'auto', 
            display: 'flex', 
            flexDirection: 'column',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '0 0 8px 8px'
          }}
        >
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              
              @keyframes fadeInUp {
                from {
                  opacity: 0;
                  transform: translateY(20px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
              
              @keyframes pulse {
                0%, 100% {
                  opacity: 0.8;
                  transform: scale(1);
                }
                50% {
                  opacity: 1;
                  transform: scale(1.05);
                }
              }
            `}
          </style>

          {/* Loading indicator for auto-loading more messages */}
          {isLoadingMore && (
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '16px',
              padding: '12px',
              fontSize: '14px',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #FF6666',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Loading previous messages...
            </div>
          )}

          {/* Small loading spinner for initial load */}
          {isLoading && allMessages.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #FF6666',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <span style={{ fontSize: '14px', color: '#666' }}>Loading messages...</span>
            </div>
          )}

          {/* Messages Container */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {recordingUrl && (
              <div style={{
                backgroundColor: '#fff',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '14px',
                marginBottom: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  alignItems: 'center',
                  marginBottom: '10px',
                  flexWrap: 'wrap'
                }}>
                  <strong style={{ color: '#333', fontSize: '14px' }}>Call Recording</strong>
                  <span style={{ color: '#666', fontSize: '12px' }}>
                    {orderId ? `Order ID: ${orderId}` : consultationMode ? `Mode: ${consultationMode}` : ''}
                  </span>
                </div>
                <AudioPlayer source={recordingUrl} />
              </div>
            )}

            {/* Small spinner for loading more messages */}
            {!recordingOnly && isLoadingMore && allMessages.length > 0 && (
              <div style={{ 
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                padding: '8px 16px',
                borderRadius: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                color: '#666'
              }}>
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #FF6666',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Loading...
              </div>
            )}

            {recordingOnly ? null : allMessages.length === 0 && !isLoadingMore && !isLoading && !recordingUrl ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px',
                color: '#666',
                fontSize: '14px'
              }}>
                No messages found
              </div>
            ) : allMessages.length === 0 && isLoading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px',
                color: '#666',
                fontSize: '14px'
              }}>
                Loading messages...
              </div>
            ) : (
              allMessages.map((msg, index) => {
                const isCurrentUser = msg?.from?.id !== userId;
                const messageKey = msg._id || `msg-${index}-${msg.createdAt}`;
                
                // Add date separator if this message is from a different day than the previous one
                const showDateSeparator = index === 0 || (
                  msg?.createdAt &&
                  allMessages[index - 1]?.createdAt &&
                  new Date(msg.createdAt).toDateString() !== new Date(allMessages[index - 1].createdAt).toDateString()
                );
                
                // Handle different message types
                let messageContent;
                let isSystemMessage = false;
                
                if (msg?.type === "audio" && msg?.metadata?.fileUrl) {
                  messageContent = <AudioPlayer source={msg.metadata.fileUrl} />;
                } else if (msg?.type === "image" && msg?.metadata?.fileUrl) {
                  // Image message
                  messageContent = (
                    <div style={{ textAlign: 'center' }}>
                      <img 
                        src={msg.metadata.fileUrl} 
                        alt={msg.metadata.filename || "Image"}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          objectFit: 'contain'
                        }}
                        onClick={() => {
                          // Open image in new tab for full view
                          window.open(msg.metadata.fileUrl, '_blank');
                        }}
                        title="Click to view full size"
                      />
                      {msg.metadata.filename && (
                        <p style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginTop: '4px',
                          wordBreak: 'break-word'
                        }}>
                          {msg.metadata.filename}
                        </p>
                      )}
                    </div>
                  );
                } else if (msg?.type === "html" && msg?.metaEditor?.html) {
                  // System message with HTML content (user details card)
                  messageContent = (
                    <div style={{
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div 
                        dangerouslySetInnerHTML={{ __html: msg.metaEditor.html }} 
                        style={{
                          position: 'relative',
                          zIndex: 1
                        }}
                      />
                      {/* Decorative background elements */}
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        left: '-10px',
                        right: '-10px',
                        bottom: '-10px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '12px',
                        zIndex: 0,
                        opacity: 0.05
                      }} />
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(45deg, rgba(255, 102, 102, 0.02) 0%, rgba(255, 137, 137, 0.02) 100%)',
                        borderRadius: '12px',
                        zIndex: 0
                      }} />
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        width: '20px',
                        height: '20px',
                        background: 'linear-gradient(45deg, #FF6666, #ff8989)',
                        borderRadius: '50%',
                        zIndex: 2,
                        opacity: 0.8,
                        animation: 'pulse 2s ease-in-out infinite'
                      }} />
                    </div>
                  );
                  isSystemMessage = true;
                } else {
                  const messageText = msg?.content || "[No message]";
                  messageContent = <div dangerouslySetInnerHTML={{ __html: messageText }} />;
                }

                return (
                  <React.Fragment key={messageKey}>
                    {/* Date Separator */}
                    {showDateSeparator && (
                      <div style={{
                        textAlign: 'center',
                        margin: '16px 0',
                        fontSize: '12px',
                        color: '#666',
                        fontWeight: '500'
                      }}>
                        {msg?.createdAt ? new Date(msg.createdAt).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                           timeZone: 'Asia/Kolkata'
                        }) : ''}
                      </div>
                    )}
                    
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: isSystemMessage ? 'center' : (isCurrentUser ? 'flex-end' : 'flex-start'),
                        marginBottom: '12px',
                        animation: isSystemMessage ? 'fadeInUp 0.6s ease-out' : 'none'
                      }}
                    >
                      <div
                        style={{
                          maxWidth: isSystemMessage ? '90%' : '70%',
                          padding: isSystemMessage ? '0' : '8px 16px',
                          borderRadius: isSystemMessage ? '12px' : '16px',
                          boxShadow: isSystemMessage ? '0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.1)',
                          fontSize: '14px',
                          backgroundColor: isSystemMessage ? 'transparent' : (isCurrentUser ? '#ff8989' : '#E5E7EB'),
                          color: isCurrentUser ? '#FFFFFF' : '#000000',
                          borderBottomRightRadius: isSystemMessage ? '12px' : (isCurrentUser ? '0px' : '16px'),
                          borderBottomLeftRadius: isSystemMessage ? '12px' : (isCurrentUser ? '16px' : '0px'),
                          border: isSystemMessage ? '1px solid rgba(255, 102, 102, 0.1)' : 'none',
                          backdropFilter: isSystemMessage ? 'blur(10px)' : 'none'
                        }}
                      >
                        {messageContent}
                        {!isSystemMessage && (
                          <p className="text-xs mt-1 opacity-70 text-right" style={{ 
                            color: isCurrentUser ? '#FFFFFF' : '#000000', 
                            fontSize: '12px' 
                          }}>
                            {msg?.from?.name} {isCurrentUser ? ':' : ':'}
                            {msg?.createdAt ? dateFormatWithTime(msg.createdAt) : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
            
            {/* Scroll to Bottom Button */}
            {showScrollToBottom && (
              <button
                onClick={scrollToBottom}
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  right: '20px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#FF6666',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  zIndex: 10
                }}
                title="Scroll to bottom"
              >
                ↓
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBox;
